import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hämtaKunder, hämtaMoten, hämtaInkopare, hämtaSäljare, hämtaKöptaArtiklar, hämtaProvningarFörInkopare, hämtaMote, uppdateraKund, uppdateraMote, taBortMote, skapaInkopare, uppdateraInkopare, taBortInkopare, Kund, Mote, AktivitetsTyp, Inkopare, Saljare, KöptArtikel, ÖppenOrder, Provning, ProvningsUtfall } from '../api/client';
import KundHistorik from '../components/KundHistorik';
import api from '../api/client';
import { TEMA, useMobil } from '../theme';

const TYP_ALTERNATIV = ['restaurang', 'grossist', 'övrigt'];
const SEGMENT_ALTERNATIV = ['A', 'B', 'C', 'D'];

const AKTIVITETS_TYP_INFO: Record<AktivitetsTyp, { etikett: string; färg: string }> = {
  möte: { etikett: 'MÖTE', färg: TEMA.positive },
  epost: { etikett: 'E-POST', färg: TEMA.inkSoft },
  samtal: { etikett: 'SAMTAL', färg: TEMA.inkSoft },
  anteckning: { etikett: 'ANTECKNING', färg: TEMA.inkSoft },
  paminnelse: { etikett: 'PÅMINNELSE', färg: TEMA.accent },
};

const AKTIVITETS_TYP_VÄLJBARA: AktivitetsTyp[] = ['möte', 'samtal', 'anteckning', 'paminnelse'];

interface RedigeraProvning {
  provning_id?: string;
  pris: string;
  vin_namn: string;
  reaktion: string;
  kommentar: string;
  pris_accepterat: string;
}

const sektionsLabel: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  fontWeight: 500,
  color: TEMA.accent,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: '0 0 12px',
};

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '16px',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  color: TEMA.inkFaint,
  margin: '0 0 4px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};

const btnPrimärStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 500,
  padding: '6px 14px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.accent}`,
  background: TEMA.accent,
  color: TEMA.onAccent,
  cursor: 'pointer',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  padding: '6px 14px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

const chipStyle = (färg: string): React.CSSProperties => ({
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  letterSpacing: '0.04em',
  padding: '3px 10px',
  borderRadius: '2px',
  border: `1px solid ${färg}`,
  color: färg,
});

const reaktionFärg = (reaktion: string): string => {
  if (reaktion === 'gillade') return TEMA.positive;
  if (reaktion === 'nej') return '#D9695F';
  return TEMA.inkFaint;
};

// En säkert kopplad provning visas med artikelnamnet från Fortnox, som har
// producent, årgång och format. Vid ny årgång eller osäker koppling visas
// namnet som kom in, eftersom artikeln då inte är exakt samma vin.
function säkerArtikel(p: any): boolean {
  return !!p.artikel_namn && (p.matchning === 'exakt' || p.matchning === 'bekraftad');
}

// Listpris från Fortnox och priset som diskuterades på mötet, var för sig.
// Skillnaden i procent är rabatten, och underlag för statistik över rabatter.
function prisdelar(p: any): { lista: number | null; pris: number | null; skillnad: number | null } {
  const lista = säkerArtikel(p) && p.listpris ? Math.round(Number(p.listpris)) : null;
  const pris = p.pris ? Math.round(Number(p.pris)) : null;
  const skillnad = lista && pris && lista !== pris ? Math.round((pris / lista - 1) * 100) : null;
  return { lista, pris, skillnad };
}

const ÅRTAL = /\b(19[5-9]\d|20[0-4]\d)\b/;
const FORMAT = /(\d{2,4})\s*cl\b/i;
const VINKOLUMNER = '62px minmax(90px, 1.1fr) minmax(140px, 2fr) 44px 76px 110px 150px';

// "25 sep"
function kortDatum(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }).replace('.', '');
}

// Utfallet av en provning: beställd, väntar på order eller inte beställd.
// Saknas utfall är vinet inte kopplat till en artikel, och då visas inget.
function Utfall({ u }: { u?: ProvningsUtfall | null }) {
  if (!u) return <span />;
  const liten: React.CSSProperties = { display: 'block', fontSize: '11.5px', color: TEMA.inkFaint, marginTop: '2px' };
  if (u.status === 'bestalld') {
    const antal = Math.round(u.antal);
    return (
      <span style={{ minWidth: 0 }}>
        <span style={{ color: TEMA.positive }}>Beställd {antal} fl</span>
        <span style={liten}>
          #{u.order} · {kortDatum(u.datum)}{u.a_pris ? ` · à ${Math.round(u.a_pris)} kr` : ''}
          {u.ordrar > 1 ? ` · ${u.ordrar} ordrar` : ''}
        </span>
      </span>
    );
  }
  if (u.status === 'vantar') {
    return (
      <span style={{ minWidth: 0, color: TEMA.inkSoft }}>
        Väntar
        <span style={liten}>{u.dagar_kvar} {u.dagar_kvar === 1 ? 'dag' : 'dagar'} kvar</span>
      </span>
    );
  }
  return <span style={{ color: TEMA.inkFaint }}>Inte beställd</span>;
}

// Delar upp en provning i producent, vin, årgång och format. Kopplade viner
// får producenten från Fortnox och resten ur artikelnamnet.
function delaVin(p: any): { producent: string; vin: string; årgång: string; format: string } {
  const säker = säkerArtikel(p);
  const namn: string = (säker ? p.artikel_namn : p.vin_namn) || '';
  const producent: string = (säker ? p.tillverkare : p.producent) || '';
  const årgång = (namn.match(ÅRTAL) || [])[1] || '';
  const f = namn.match(FORMAT);
  const format = f ? `${f[1]} cl` : (säker ? '' : p.format || '');
  let vin = namn.replace(FORMAT, '').replace(ÅRTAL, '');
  if (producent) {
    const esc = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const efternamn = producent.trim().split(/\s+/).pop() || '';
    const helt = new RegExp(esc(producent), 'i');
    vin = helt.test(vin)
      ? vin.replace(helt, '')
      // Förkortat i artikelnamnet, som "B. Dubois" eller "Bonnardot:". Bara i början av namnet.
      : vin.replace(new RegExp(`^\\s*(?:[A-ZÀ-Ý]\\.\\s*)?${esc(efternamn)}\\b\\s*[:\\-–]?`, 'i'), '');
  }
  vin = vin.replace(/\s+[-–:]\s+/g, ' ').replace(/^[\s\-–:.]+|[\s\-–:.]+$/g, '').replace(/\s{2,}/g, ' ').trim();
  return { producent, vin: vin || namn, årgång, format };
}

export default function KundVy() {
  const { kund_id } = useParams();
  const navigate = useNavigate();
  const [kund, setKund] = useState<Kund | null>(null);
  const [moten, setMoten] = useState<Mote[]>([]);
  const [inkopare, setInkopare] = useState<Inkopare[]>([]);
  const [fakturor, setFakturor] = useState<any[]>([]);
  const ärMobil = useMobil();
  const [säljare, setSäljare] = useState<Saljare[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const [dropdownÖppen, setDropdownÖppen] = useState(false);
  const [sparar, setSparar] = useState(false);

  const [redigerarInfo, setRedigerarInfo] = useState(false);
  const [infoUtkast, setInfoUtkast] = useState({ typ: '', stad: '', matkoncept: '', segment: '' });
  const [spararInfo, setSpararInfo] = useState(false);
  const [infoFel, setInfoFel] = useState<string | null>(null);

  const [visaNyInkopare, setVisaNyInkopare] = useState(false);
  const tomInkopareUtkast = { namn: '', roll: '', telefon: '', email: '', föredrar_typ: '', prisnivå: 'medel', smakprofil_text: '' };
  const [nyInkopareUtkast, setNyInkopareUtkast] = useState(tomInkopareUtkast);
  const [skaparInkopare, setSkaparInkopare] = useState(false);
  const [nyInkopareFel, setNyInkopareFel] = useState<string | null>(null);

  const [redigerarInkopareId, setRedigerarInkopareId] = useState<string | null>(null);
  const [redigerarInkopareUtkast, setRedigerarInkopareUtkast] = useState(tomInkopareUtkast);
  const [spararInkopare, setSpararInkopare] = useState(false);
  const [redigerarInkopareFel, setRedigerarInkopareFel] = useState<string | null>(null);
  const [raderarInkopareId, setRaderarInkopareId] = useState<string | null>(null);

  const [artiklar, setArtiklar] = useState<KöptArtikel[]>([]);
  // Ordrar som väntar på fakturering. De är inte köp ännu och visas för sig.
  const [öppnaOrdrar, setÖppnaOrdrar] = useState<ÖppenOrder[]>([]);
  const [laddarArtiklar, setLaddarArtiklar] = useState(false);
  const [artiklarFel, setArtiklarFel] = useState<string | null>(null);
  const [visaAntal, setVisaAntal] = useState(10);

  const [expanderadInkopare, setExpanderadInkopare] = useState<string | null>(null);
  const [provningarPerInkopare, setProvningarPerInkopare] = useState<Record<string, Provning[]>>({});
  const [öppnaProvningar, setÖppnaProvningar] = useState<Record<string, Provning[] | 'laddar'>>({});
  const [laddarProvningar, setLaddarProvningar] = useState<Record<string, boolean>>({});

  const [redigerarMoteId, setRedigerarMoteId] = useState<string | null>(null);
  const tomMoteUtkast = { typ: 'möte' as AktivitetsTyp, datum: '', nästa_kontakt: '', notat: '' };
  const [redigerarMoteUtkast, setRedigerarMoteUtkast] = useState(tomMoteUtkast);
  const [redigerarMoteProvningar, setRedigerarMoteProvningar] = useState<RedigeraProvning[]>([]);
  const [laddarRedigerarMote, setLaddarRedigerarMote] = useState(false);
  const [spararMote, setSpararMote] = useState(false);
  const [redigerarMoteFel, setRedigerarMoteFel] = useState<string | null>(null);
  const [snabbgranskarId, setSnabbgranskarId] = useState<string | null>(null);
  const [raderarMoteId, setRaderarMoteId] = useState<string | null>(null);

  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLaddar(true);
    setFel(null);
    Promise.all([hämtaKunder(), hämtaMoten(), hämtaInkopare(), hämtaSäljare()])
      .then(([k, m, i, s]) => {
        const kundData = k.data.find((k: Kund) => k.kund_id === kund_id) || null;
        setKund(kundData);
        setMoten(m.data.filter((m: Mote) => m.kund_id === kund_id));
        setInkopare(i.data.filter((i: Inkopare) => i.kund_id === kund_id));
        setSäljare(s.data);
        setLaddar(false);

        if (kundData) {
          api.get(`/fortnox/fakturor-per-kund?kund_nr=${encodeURIComponent(kundData.fortnox_kund_nr || '')}`)
            .then(res => {
              if (res.data.hittad) {
                setFakturor(res.data.fakturor);
              }
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        console.error('Kunde inte ladda kunddata:', err);
        setFel('Kunde inte ladda kundens information just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, [kund_id]);

  useEffect(() => {
    if (!kund) return;
    let aktuell = true;
    setLaddarArtiklar(true);
    setArtiklarFel(null);
    hämtaKöptaArtiklar(kund.fortnox_kund_nr || '')
      .then(res => {
        if (!aktuell) return;
        if (res.data.hittad) {
          setArtiklar(res.data.artiklar);
        }
        setÖppnaOrdrar(res.data.oppna_ordrar || []);
        setLaddarArtiklar(false);
      })
      .catch(err => {
        if (!aktuell) return;
        console.error('Kunde inte ladda köphistorik:', err);
        setArtiklarFel('Kunde inte ladda köphistorik just nu.');
        setLaddarArtiklar(false);
      });
    return () => { aktuell = false; };
  }, [kund]);

  useEffect(() => {
    function hanteraKlick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownÖppen(false);
      }
    }
    if (dropdownÖppen) {
      document.addEventListener('mousedown', hanteraKlick);
      return () => document.removeEventListener('mousedown', hanteraKlick);
    }
  }, [dropdownÖppen]);

  async function väljSäljare(namn: string) {
    if (!kund || namn === kund.ansvarig_säljare) {
      setDropdownÖppen(false);
      return;
    }
    if (!window.confirm(`Byt ansvarig säljare till ${namn}?`)) {
      setDropdownÖppen(false);
      return;
    }
    setSparar(true);
    try {
      await uppdateraKund(kund.kund_id, {
        namn: kund.namn,
        typ: kund.typ,
        segment: kund.segment,
        stad: kund.stad,
        matkoncept: kund.matkoncept,
        ansvarig_säljare: namn,
        aktiv: kund.aktiv,
        fortnox_kund_nr: kund.fortnox_kund_nr,
      });
      setKund({ ...kund, ansvarig_säljare: namn });
      setDropdownÖppen(false);
    } catch (err) {
      console.error('Kunde inte uppdatera ansvarig säljare:', err);
      alert('Kunde inte spara ändringen. Försök igen.');
    } finally {
      setSparar(false);
    }
  }

  function börjaRedigeraInfo() {
    if (!kund) return;
    setInfoUtkast({
      typ: kund.typ || '',
      stad: kund.stad || '',
      matkoncept: kund.matkoncept || '',
      segment: kund.segment || '',
    });
    setInfoFel(null);
    setRedigerarInfo(true);
  }

  function avbrytRedigeraInfo() {
    setRedigerarInfo(false);
    setInfoFel(null);
  }

  async function sparaInfo() {
    if (!kund) return;
    if (!window.confirm('Spara ändringarna?')) return;
    setSpararInfo(true);
    setInfoFel(null);
    try {
      await uppdateraKund(kund.kund_id, {
        namn: kund.namn,
        typ: infoUtkast.typ,
        segment: infoUtkast.segment,
        stad: infoUtkast.stad,
        matkoncept: infoUtkast.matkoncept,
        ansvarig_säljare: kund.ansvarig_säljare,
        aktiv: kund.aktiv,
        fortnox_kund_nr: kund.fortnox_kund_nr,
      });
      setKund({
        ...kund,
        typ: infoUtkast.typ,
        segment: infoUtkast.segment,
        stad: infoUtkast.stad,
        matkoncept: infoUtkast.matkoncept,
      });
      setRedigerarInfo(false);
    } catch (err) {
      console.error('Kunde inte uppdatera kundinformation:', err);
      setInfoFel('Kunde inte spara ändringarna. Försök igen.');
    } finally {
      setSpararInfo(false);
    }
  }

  function öppnaNyInkopare() {
    setNyInkopareUtkast(tomInkopareUtkast);
    setNyInkopareFel(null);
    setVisaNyInkopare(true);
  }

  function avbrytNyInkopare() {
    setVisaNyInkopare(false);
    setNyInkopareFel(null);
  }

  async function sparaNyInkopare() {
    if (!kund) return;
    if (!nyInkopareUtkast.namn.trim()) {
      setNyInkopareFel('Namn krävs.');
      return;
    }
    setSkaparInkopare(true);
    setNyInkopareFel(null);
    try {
      const res = await skapaInkopare({ kund_id: kund.kund_id, ...nyInkopareUtkast });
      const nyId = (res.data as any).inkopare_id;
      setInkopare(prev => [...prev, { kund_id: kund.kund_id, inkopare_id: nyId, ...nyInkopareUtkast }]);
      setVisaNyInkopare(false);
    } catch (err) {
      console.error('Kunde inte skapa inköpare:', err);
      setNyInkopareFel('Kunde inte spara inköparen. Försök igen.');
    } finally {
      setSkaparInkopare(false);
    }
  }

  function börjaRedigeraInkopare(i: Inkopare) {
    const id = i.inkopare_id || i.inköpare_id || '';
    setRedigerarInkopareId(id);
    setRedigerarInkopareUtkast({
      namn: i.namn || '',
      roll: i.roll || '',
      telefon: i.telefon || '',
      email: i.email || '',
      föredrar_typ: i.föredrar_typ || '',
      prisnivå: i.prisnivå || 'medel',
      smakprofil_text: i.smakprofil_text || '',
    });
    setRedigerarInkopareFel(null);
  }

  function avbrytRedigeraInkopare() {
    setRedigerarInkopareId(null);
    setRedigerarInkopareFel(null);
  }

  async function sparaRedigeraInkopare() {
    if (!redigerarInkopareId || !kund) return;
    if (!redigerarInkopareUtkast.namn.trim()) {
      setRedigerarInkopareFel('Namn krävs.');
      return;
    }
    if (!window.confirm('Spara ändringarna?')) return;
    setSpararInkopare(true);
    setRedigerarInkopareFel(null);
    try {
            // Behåll extra-adresserna; de finns inte i redigeringsformuläret.
      const befintlig = inkopare.find(x => ((x as any).inkopare_id || (x as any).inköpare_id) === redigerarInkopareId);
      await uppdateraInkopare(redigerarInkopareId, {
        kund_id: kund.kund_id,
        ...redigerarInkopareUtkast,
        extra_epost: befintlig?.extra_epost || [],
      });
      setInkopare(prev => prev.map(i => {
        const id = i.inkopare_id || i.inköpare_id || '';
        return id === redigerarInkopareId ? { ...i, ...redigerarInkopareUtkast } : i;
      }));
      setRedigerarInkopareId(null);
    } catch (err) {
      console.error('Kunde inte uppdatera inköpare:', err);
      setRedigerarInkopareFel('Kunde inte spara ändringarna. Försök igen.');
    } finally {
      setSpararInkopare(false);
    }
  }

    // Extra-adresser läggs till automatiskt när en aktivitet kopplas i Att koppla.
  // Här kan en felaktig adress tas bort; nästa koppling lär in rätt adress.
  async function taBortExtraEpost(i: Inkopare, adress: string) {
    if (!window.confirm(`Ta bort ${adress} från ${i.namn}?`)) return;
    const id = (i as any).inkopare_id || (i as any).inköpare_id;
    try {
      const uppdaterad = { ...i, extra_epost: (i.extra_epost || []).filter(e => e !== adress) };
      const { inkopare_id, inköpare_id, ...data } = uppdaterad as any;
      await uppdateraInkopare(id, data);
      setInkopare(prev => prev.map(x =>
        ((x as any).inkopare_id || (x as any).inköpare_id) === id ? uppdaterad : x
      ));
    } catch (err) {
      console.error('Kunde inte ta bort adressen:', err);
      alert('Kunde inte ta bort adressen. Försök igen.');
    }
  }


  async function raderaInkopare(i: Inkopare) {
    const id = i.inkopare_id || i.inköpare_id;
    if (!id) return;
    if (!window.confirm(`Ta bort ${i.namn} som inköpare? Det går inte att ångra.`)) return;
    setRaderarInkopareId(id);
    try {
      await taBortInkopare(id);
      setInkopare(prev => prev.filter(x => (x.inkopare_id || x.inköpare_id) !== id));
      if (redigerarInkopareId === id) setRedigerarInkopareId(null);
    } catch (err) {
      console.error('Kunde inte ta bort inköpare:', err);
      alert('Kunde inte ta bort inköparen. Försök igen.');
    } finally {
      setRaderarInkopareId(null);
    }
  }

  function toggleInkopare(inkopareId: string | undefined) {
    if (!inkopareId) return;
    if (expanderadInkopare === inkopareId) {
      setExpanderadInkopare(null);
      return;
    }
    setExpanderadInkopare(inkopareId);
    if (!provningarPerInkopare[inkopareId]) {
      setLaddarProvningar(prev => ({ ...prev, [inkopareId]: true }));
      hämtaProvningarFörInkopare(inkopareId)
        .then(res => {
          setProvningarPerInkopare(prev => ({ ...prev, [inkopareId]: res.data }));
          setLaddarProvningar(prev => ({ ...prev, [inkopareId]: false }));
        })
        .catch(err => {
          console.error('Kunde inte ladda provningshistorik:', err);
          setLaddarProvningar(prev => ({ ...prev, [inkopareId]: false }));
        });
    }
  }
  // Vinerna hämtas först när någon vill se dem, så att kortet är snabbt att ladda.
  async function växlaProvningar(m: Mote) {
    if (öppnaProvningar[m.mote_id]) {
      setÖppnaProvningar(prev => {
        const kopia = { ...prev };
        delete kopia[m.mote_id];
        return kopia;
      });
      return;
    }
    setÖppnaProvningar(prev => ({ ...prev, [m.mote_id]: 'laddar' }));
    try {
      const res = await hämtaMote(m.mote_id);
      setÖppnaProvningar(prev => ({ ...prev, [m.mote_id]: res.data.provningar || [] }));
    } catch (err) {
      console.error('Kunde inte ladda provningarna:', err);
      setÖppnaProvningar(prev => {
        const kopia = { ...prev };
        delete kopia[m.mote_id];
        return kopia;
      });
      alert('Kunde inte ladda vinerna. Försök igen.');
    }
  }

  async function börjaRedigeraMote(m: Mote) {
    setRedigerarMoteId(m.mote_id);
    setRedigerarMoteUtkast({
      typ: m.typ,
      datum: m.datum,
      nästa_kontakt: m.nästa_kontakt || '',
      notat: m.notat || '',
    });
    setRedigerarMoteProvningar([]);
    setRedigerarMoteFel(null);
    setLaddarRedigerarMote(true);
    try {
      const res = await hämtaMote(m.mote_id);
      setRedigerarMoteProvningar(
        (res.data.provningar || []).map(p => ({
          provning_id: (p as any).provning_id,
          pris: (p as any).pris != null ? String((p as any).pris) : '',
          vin_namn: p.vin_namn || '',
          reaktion: p.reaktion || 'neutral',
          kommentar: p.kommentar || '',
          pris_accepterat: p.pris_accepterat === true ? 'ja' : p.pris_accepterat === false ? 'nej' : 'vet ej',
        }))
      );
    } catch (err) {
      console.error('Kunde inte ladda aktivitetens provningar:', err);
      setRedigerarMoteFel('Kunde inte ladda provningarna för den här aktiviteten. Du kan fortfarande redigera övriga fält.');
    } finally {
      setLaddarRedigerarMote(false);
    }
  }

  function avbrytRedigeraMote() {
    setRedigerarMoteId(null);
    setRedigerarMoteFel(null);
  }

  function läggTillRedigeraProvning() {
    setRedigerarMoteProvningar(prev => [...prev, { vin_namn: '', reaktion: 'neutral', kommentar: '', pris_accepterat: 'vet ej', pris: '' }]);
  }

  function taBortRedigeraProvning(index: number) {
    setRedigerarMoteProvningar(prev => prev.filter((_, i) => i !== index));
  }

  function uppdateraRedigeraProvning(index: number, fält: keyof RedigeraProvning, värde: string) {
    setRedigerarMoteProvningar(prev => prev.map((p, i) => i === index ? { ...p, [fält]: värde } : p));
  }

  async function sparaRedigeraMote() {
    if (!redigerarMoteId || !kund) return;
    const m = moten.find(x => x.mote_id === redigerarMoteId);
    if (!m) return;
    setSpararMote(true);
    setRedigerarMoteFel(null);
    try {
      const provningarMappade = redigerarMoteUtkast.typ === 'möte' || redigerarMoteUtkast.typ === 'epost'
        ? redigerarMoteProvningar
            .filter(p => p.vin_namn.trim())
            .map(p => ({
              provning_id: p.provning_id,
              pris: p.pris.trim() ? Number(p.pris.replace(',', '.').replace(/[^\d.]/g, '')) || null : null,
              vin_namn: p.vin_namn,
              reaktion: p.reaktion,
              kommentar: p.kommentar,
              pris_accepterat: p.pris_accepterat === 'ja' ? true : p.pris_accepterat === 'nej' ? false : null,
            }))
        : [];
      await uppdateraMote(redigerarMoteId, {
        kund_id: m.kund_id,
        inkopare_id: m.inkopare_id || null,
        säljare: m.säljare,
        datum: redigerarMoteUtkast.datum,
        nästa_kontakt: redigerarMoteUtkast.nästa_kontakt || null,
        notat: redigerarMoteUtkast.notat,
        typ: redigerarMoteUtkast.typ,
        granskad: true,
        avklarad: m.avklarad,
        provningar: provningarMappade,
      });
      // Hämta vinerna på nytt, så att den utfällda listan visar det som sparades.
      if (öppnaProvningar[redigerarMoteId]) {
        const färsk = await hämtaMote(redigerarMoteId);
        setÖppnaProvningar(prev => ({ ...prev, [redigerarMoteId]: färsk.data.provningar || [] }));
      }
      setMoten(prev => prev.map(x => x.mote_id === redigerarMoteId ? {
        ...x,
        typ: redigerarMoteUtkast.typ,
        datum: redigerarMoteUtkast.datum,
        nästa_kontakt: redigerarMoteUtkast.nästa_kontakt,
        notat: redigerarMoteUtkast.notat,
        granskad: true,
      } : x));
      setRedigerarMoteId(null);
    } catch (err) {
      console.error('Kunde inte uppdatera aktiviteten:', err);
      setRedigerarMoteFel('Kunde inte spara ändringarna. Försök igen.');
    } finally {
      setSpararMote(false);
    }
  }

  async function snabbGranska(m: Mote) {
    setSnabbgranskarId(m.mote_id);
    try {
      const full = await hämtaMote(m.mote_id);
      await uppdateraMote(m.mote_id, {
        kund_id: m.kund_id,
        inkopare_id: m.inkopare_id || null,
        säljare: m.säljare,
        datum: m.datum,
        nästa_kontakt: m.nästa_kontakt || null,
        notat: m.notat,
        typ: m.typ,
        granskad: true,
        avklarad: m.avklarad,
        provningar: full.data.provningar || [],
      });
      setMoten(prev => prev.map(x => x.mote_id === m.mote_id ? { ...x, granskad: true } : x));
    } catch (err) {
      console.error('Kunde inte markera aktiviteten som granskad:', err);
      alert('Kunde inte markera som granskad. Försök igen.');
    } finally {
      setSnabbgranskarId(null);
    }
  }

  async function raderaMote(m: Mote) {
    if (!window.confirm('Ta bort den här aktiviteten? Det går inte att ångra.')) return;
    setRaderarMoteId(m.mote_id);
    try {
      await taBortMote(m.mote_id);
      setMoten(prev => prev.filter(x => x.mote_id !== m.mote_id));
      if (redigerarMoteId === m.mote_id) setRedigerarMoteId(null);
    } catch (err) {
      console.error('Kunde inte ta bort aktiviteten:', err);
      alert('Kunde inte ta bort aktiviteten. Försök igen.');
    } finally {
      setRaderarMoteId(null);
    }
  }

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;
  if (fel) return (
    <div>
      <button onClick={() => navigate('/-1')} style={{
        fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, background: 'none', border: 'none',
        cursor: 'pointer', padding: 0, marginBottom: '16px'
      }}>
        ← Tillbaka
      </button>
      <p style={{ color: '#D9695F', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );
  if (!kund) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Kund hittades inte.</p>;

  return (
    <div>
      <button onClick={() => navigate(-1)} style={{
        fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, background: 'none', border: 'none',
        cursor: 'pointer', padding: 0, marginBottom: '20px'
      }}>
        ← Tillbaka
      </button>

      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px 12px', marginBottom: '28px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '24px', color: TEMA.ink, margin: 0 }}>{kund.namn}</h1>
        <span style={chipStyle(TEMA.inkSoft)}>Segment {kund.segment}</span>
        <span style={chipStyle(kund.fortnox_kund_nr ? TEMA.positive : TEMA.inkSoft)}>
          {kund.fortnox_kund_nr ? 'Kund' : 'Prospect'}
        </span>
        {kund.intern && (
          <span style={chipStyle(TEMA.inkSoft)} title="Försäljning hit räknas inte i Statistik">Internt</span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <p style={{ ...sektionsLabel, margin: 0 }}>Info</p>
        {!redigerarInfo ? (
          <button onClick={börjaRedigeraInfo} style={btnGhostStyle}>
            Redigera
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={avbrytRedigeraInfo} disabled={spararInfo} style={btnGhostStyle}>
              Avbryt
            </button>
            <button onClick={sparaInfo} disabled={spararInfo} style={{ ...btnPrimärStyle, opacity: spararInfo ? 0.6 : 1 }}>
              {spararInfo ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        )}
      </div>

      <div style={kortStyle}>
        {infoFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 16px', fontFamily: "'Inter', sans-serif" }}>{infoFel}</p>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '18px' }}>
          <div>
            <p style={labelStyle}>Typ</p>
            {redigerarInfo ? (
              <select
                value={infoUtkast.typ}
                onChange={e => setInfoUtkast(u => ({ ...u, typ: e.target.value }))}
                style={inputStyle}
              >
                {TYP_ALTERNATIV.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            ) : (
              <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{kund.typ ? kund.typ.charAt(0).toUpperCase() + kund.typ.slice(1) : ''}</p>
            )}
          </div>
          <div>
            <p style={labelStyle}>Stad</p>
            {redigerarInfo ? (
              <input
                type="text"
                value={infoUtkast.stad}
                onChange={e => setInfoUtkast(u => ({ ...u, stad: e.target.value }))}
                style={inputStyle}
              />
            ) : (
              <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{kund.stad}</p>
            )}
          </div>
          <div>
            <p style={labelStyle}>Matkoncept</p>
            {redigerarInfo ? (
              <input
                type="text"
                value={infoUtkast.matkoncept}
                onChange={e => setInfoUtkast(u => ({ ...u, matkoncept: e.target.value }))}
                style={inputStyle}
              />
            ) : (
              <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{kund.matkoncept}</p>
            )}
          </div>
          <div>
            <p style={labelStyle}>Segment</p>
            {redigerarInfo ? (
              <select
                value={infoUtkast.segment}
                onChange={e => setInfoUtkast(u => ({ ...u, segment: e.target.value }))}
                style={inputStyle}
              >
                {SEGMENT_ALTERNATIV.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{kund.segment}</p>
            )}
          </div>
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <p style={labelStyle}>Ansvarig säljare</p>
            <button
              onClick={() => setDropdownÖppen(o => !o)}
              disabled={sparar}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontFamily: "'Inter', sans-serif",
                fontSize: '13px', fontWeight: 500, padding: '7px 12px',
                borderRadius: '2px', border: `1px solid ${TEMA.border}`,
                background: dropdownÖppen ? TEMA.bg : 'transparent',
                color: kund.ansvarig_säljare ? TEMA.ink : TEMA.inkFaint,
                cursor: sparar ? 'default' : 'pointer',
                opacity: sparar ? 0.6 : 1,
                transition: 'background 0.15s',
              }}
            >
              {sparar ? 'Sparar...' : (kund.ansvarig_säljare || 'Ej tilldelad')}
              <svg width="10" height="6" viewBox="0 0 10 6" style={{
                transform: dropdownÖppen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s', flexShrink: 0,
              }}>
                <path d="M1 1L5 5L9 1" stroke={TEMA.inkFaint} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {dropdownÖppen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`,
                borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                minWidth: '160px', zIndex: 20, overflow: 'hidden',
                padding: '4px',
              }}>
                {säljare.length === 0 && (
                  <p style={{ margin: 0, padding: '8px 10px', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                    Inga säljare hittades
                  </p>
                )}
                {säljare.map(s => {
                  const vald = s.namn === kund.ansvarig_säljare;
                  return (
                    <div
                      key={s.id}
                      onClick={() => väljSäljare(s.namn)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 10px', fontSize: '13px', fontFamily: "'Inter', sans-serif", borderRadius: '4px',
                        cursor: 'pointer', color: vald ? TEMA.positive : TEMA.ink,
                        background: vald ? 'rgba(95,174,138,0.1)' : 'transparent',
                        fontWeight: vald ? 500 : 400,
                      }}
                      onMouseEnter={e => { if (!vald) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={e => { if (!vald) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      {s.namn}
                      {vald && (
                        <svg width="12" height="10" viewBox="0 0 12 10" style={{ flexShrink: 0 }}>
                          <path d="M1 5L4.5 8.5L11 1" stroke={TEMA.positive} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <p style={labelStyle}>Möten totalt</p>
            <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{moten.length}</p>
          </div>
          <div>
            <p style={labelStyle}>Kundnr i Fortnox</p>
            <p style={{
              margin: 0, fontSize: '14px',
              fontFamily: kund.fortnox_kund_nr ? "'IBM Plex Mono', monospace" : "'Inter', sans-serif",
              color: kund.fortnox_kund_nr ? TEMA.ink : TEMA.inkFaint,
            }}>
              {kund.fortnox_kund_nr || '—'}
            </p>
          </div>
        </div>

        {/* Internt bolag (DFG KRAN ApS). Sätts i databasen, inte i appen:
            kunder.intern = true. Visas bara på den kund det gäller. */}
        {kund.intern && (
          <p style={{
            margin: '18px 0 0', paddingTop: '14px', borderTop: `1px solid ${TEMA.border}`,
            fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif",
          }}>
            Internt bolag: försäljning hit räknas inte som försäljning i Statistik, men visas för sig.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '28px', marginBottom: '12px' }}>
        <p style={{ ...sektionsLabel, margin: 0 }}>Inköpare</p>
        {!visaNyInkopare && (
          <button onClick={öppnaNyInkopare} style={btnGhostStyle}>
            + Lägg till inköpare
          </button>
        )}
      </div>

      <div style={kortStyle}>
        {visaNyInkopare && (
          <div style={{ background: TEMA.bg, border: `1px solid ${TEMA.border}`, borderRadius: '6px', padding: '14px', marginBottom: '14px' }}>
            {nyInkopareFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 10px', fontFamily: "'Inter', sans-serif" }}>{nyInkopareFel}</p>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <input placeholder="Namn" value={nyInkopareUtkast.namn} onChange={e => setNyInkopareUtkast(u => ({ ...u, namn: e.target.value }))} style={inputStyle} />
              <input placeholder="Roll" value={nyInkopareUtkast.roll} onChange={e => setNyInkopareUtkast(u => ({ ...u, roll: e.target.value }))} style={inputStyle} />
              <select value={nyInkopareUtkast.prisnivå} onChange={e => setNyInkopareUtkast(u => ({ ...u, prisnivå: e.target.value }))} style={inputStyle}>
                <option value="låg">låg</option>
                <option value="medel">medel</option>
                <option value="hög">hög</option>
              </select>
              <input placeholder="Telefon" value={nyInkopareUtkast.telefon} onChange={e => setNyInkopareUtkast(u => ({ ...u, telefon: e.target.value }))} style={inputStyle} />
              <input placeholder="E-post" value={nyInkopareUtkast.email} onChange={e => setNyInkopareUtkast(u => ({ ...u, email: e.target.value }))} style={inputStyle} />
              <input placeholder="Föredrar typ" value={nyInkopareUtkast.föredrar_typ} onChange={e => setNyInkopareUtkast(u => ({ ...u, föredrar_typ: e.target.value }))} style={inputStyle} />
            </div>
            <input
              placeholder="Smakprofil"
              value={nyInkopareUtkast.smakprofil_text}
              onChange={e => setNyInkopareUtkast(u => ({ ...u, smakprofil_text: e.target.value }))}
              style={{ ...inputStyle, marginBottom: '10px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={sparaNyInkopare} disabled={skaparInkopare} style={{ ...btnPrimärStyle, opacity: skaparInkopare ? 0.6 : 1 }}>
                {skaparInkopare ? 'Sparar...' : 'Spara'}
              </button>
              <button onClick={avbrytNyInkopare} disabled={skaparInkopare} style={btnGhostStyle}>
                Avbryt
              </button>
            </div>
          </div>
        )}

        {inkopare.length === 0 && !visaNyInkopare && <p style={{ color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Inga inköpare registrerade.</p>}
        {inkopare.map((i, idx) => {
          const inkopareId = i.inkopare_id || i.inköpare_id || '';
          const expanderad = expanderadInkopare === inkopareId;
          const redigerar = redigerarInkopareId === inkopareId;
          const provningar = provningarPerInkopare[inkopareId] || [];
          const sistaIListan = idx === inkopare.length - 1;
          return (
            <div key={inkopareId} style={{
              borderBottom: sistaIListan ? 'none' : `1px solid ${TEMA.border}`,
            }}>
              {redigerar ? (
                <div style={{ padding: '14px' }}>
                  {redigerarInkopareFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 10px', fontFamily: "'Inter', sans-serif" }}>{redigerarInkopareFel}</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <input placeholder="Namn" value={redigerarInkopareUtkast.namn} onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, namn: e.target.value }))} style={inputStyle} />
                    <input placeholder="Roll" value={redigerarInkopareUtkast.roll} onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, roll: e.target.value }))} style={inputStyle} />
                    <select value={redigerarInkopareUtkast.prisnivå} onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, prisnivå: e.target.value }))} style={inputStyle}>
                      <option value="låg">låg</option>
                      <option value="medel">medel</option>
                      <option value="hög">hög</option>
                    </select>
                    <input placeholder="Telefon" value={redigerarInkopareUtkast.telefon} onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, telefon: e.target.value }))} style={inputStyle} />
                    <input placeholder="E-post" value={redigerarInkopareUtkast.email} onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, email: e.target.value }))} style={inputStyle} />
                    <input placeholder="Föredrar typ" value={redigerarInkopareUtkast.föredrar_typ} onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, föredrar_typ: e.target.value }))} style={inputStyle} />
                  </div>
                  <input
                    placeholder="Smakprofil"
                    value={redigerarInkopareUtkast.smakprofil_text}
                    onChange={e => setRedigerarInkopareUtkast(u => ({ ...u, smakprofil_text: e.target.value }))}
                    style={{ ...inputStyle, marginBottom: '10px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={sparaRedigeraInkopare} disabled={spararInkopare} style={{ ...btnPrimärStyle, opacity: spararInkopare ? 0.6 : 1 }}>
                        {spararInkopare ? 'Sparar...' : 'Spara'}
                      </button>
                      <button onClick={avbrytRedigeraInkopare} disabled={spararInkopare} style={btnGhostStyle}>
                        Avbryt
                      </button>
                    </div>
                    <button
                      onClick={() => raderaInkopare(i)}
                      disabled={raderarInkopareId === (i.inkopare_id || i.inköpare_id)}
                      style={{ ...btnGhostStyle, color: '#D9695F', borderColor: '#D9695F' }}
                    >
                      {raderarInkopareId === (i.inkopare_id || i.inköpare_id) ? 'Tar bort...' : 'Ta bort inköpare'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    onClick={() => toggleInkopare(inkopareId)}
                    style={{
                      padding: '14px', cursor: 'pointer', position: 'relative',
                      ...(ärMobil ? { display: 'flex', flexDirection: 'column' } : {}),
                    }}
                  >
                    <div style={ärMobil ? {
                      order: 1, display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px',
                    } : {
                      position: 'absolute', top: '14px', right: '14px',
                      display: 'flex', alignItems: 'center', gap: '10px',
                    }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); börjaRedigeraInkopare(i); }}
                        style={{ ...btnGhostStyle, fontSize: '11px', padding: '4px 10px' }}
                      >
                        Redigera
                      </button>
                      <span style={{ fontSize: '11px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                        {expanderad ? 'Dölj provningshistorik' : 'Visa provningshistorik'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', paddingRight: ärMobil ? 0 : '220px' }}>
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(224,168,62,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', fontWeight: 500, color: TEMA.accent, flexShrink: 0,
                      }}>
                        {i.namn?.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink }}>{i.namn}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{i.roll}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', fontSize: '12px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                      {i.telefon && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" fill="none" stroke={TEMA.inkSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {i.telefon}
                        </span>
                      )}
                      {i.email && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                            <rect x="2" y="4" width="20" height="16" rx="2" fill="none" stroke={TEMA.inkSoft} strokeWidth="2" />
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" fill="none" stroke={TEMA.inkSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {i.email}
                        </span>
                      )}
                      {i.föredrar_typ && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                            <path d="M8 22h8M12 11v11M5 2h14l-1.5 7a5.5 5.5 0 0 1-11 0z" fill="none" stroke={TEMA.inkSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {i.föredrar_typ}
                        </span>
                      )}
                    </div>
                                        {(i.extra_epost || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {(i.extra_epost || []).map(adress => (
                          <span key={adress} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '3px 8px', borderRadius: '3px',
                            border: `1px solid ${TEMA.border}`,
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint,
                          }}>
                            {adress}
                            <button
                              onClick={() => taBortExtraEpost(i, adress)}
                              title="Ta bort adressen"
                              style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                color: TEMA.inkFaint, fontSize: '13px', lineHeight: 1,
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {i.smakprofil_text && (
                      <p style={{ margin: '8px 0 0', fontSize: '13px', color: TEMA.inkSoft, fontStyle: 'italic', fontFamily: "'Inter', sans-serif" }}>
                        "{i.smakprofil_text}"
                      </p>
                    )}
                  </div>

                  {expanderad && (
                    <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${TEMA.border}` }}>
                      {laddarProvningar[inkopareId] && (
                        <p style={{ fontSize: '13px', color: TEMA.inkFaint, margin: '12px 0 0', fontFamily: "'Inter', sans-serif" }}>Laddar provningshistorik...</p>
                      )}
                      {!laddarProvningar[inkopareId] && provningar.length === 0 && (
                        <p style={{ fontSize: '13px', color: TEMA.inkFaint, margin: '12px 0 0', fontFamily: "'Inter', sans-serif" }}>Inga provningar registrerade ännu.</p>
                      )}
                      {!laddarProvningar[inkopareId] && provningar.map((p, idx) => {
                        const färg = reaktionFärg(p.reaktion);
                        return (
                          <div key={p.provning_id || idx} style={{
                            marginTop: '12px', paddingTop: '12px',
                            borderTop: idx > 0 ? `1px solid ${TEMA.border}` : 'none',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontFamily: "'Fraunces', serif", fontSize: '14px', color: TEMA.ink }}>{p.vin_namn}</span>
                              <span style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: '10px', padding: '2px 8px', borderRadius: '2px',
                                border: `1px solid ${färg}`, color: färg,
                              }}>
                                {p.reaktion}
                              </span>
                            </div>
                            <p style={{ margin: 0, fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                              {p.datum}
                              {p.pris_accepterat !== null && (p.pris_accepterat ? ' · Pris accepterat' : ' · Pris ej accepterat')}
                            </p>
                            {p.kommentar && (
                              <p style={{ margin: '4px 0 0', fontSize: '13px', color: TEMA.inkSoft, fontStyle: 'italic', fontFamily: "'Inter', sans-serif" }}>
                                "{p.kommentar}"
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '28px', marginBottom: '12px' }}>
        <p style={{ ...sektionsLabel, margin: 0 }}>Aktivitetshistorik</p>
        <button onClick={() => navigate(`/registrera?kund_id=${kund_id}`)} style={btnPrimärStyle}>
          + Ny aktivitet
        </button>
      </div>

      <div style={kortStyle}>
        {moten.length === 0 && <p style={{ color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Inga aktiviteter registrerade.</p>}
        {[...moten].sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime()).map((m, idx, sorterade) => {
          const info = AKTIVITETS_TYP_INFO[m.typ] || AKTIVITETS_TYP_INFO.anteckning;
          const ogranskad = m.typ === 'epost' && !m.granskad;
          const redigerar = redigerarMoteId === m.mote_id;
          const visarVinfält = redigerarMoteUtkast.typ === 'möte' || redigerarMoteUtkast.typ === 'epost';
          const valbaraTyper = m.typ === 'epost' ? (['epost', ...AKTIVITETS_TYP_VÄLJBARA] as AktivitetsTyp[]) : AKTIVITETS_TYP_VÄLJBARA;

          return (
            <div key={m.mote_id} style={{
              padding: '14px',
              background: TEMA.bg,
              border: `1px solid ${ogranskad ? TEMA.accent : TEMA.border}`,
              borderRadius: '6px',
              marginBottom: idx < sorterade.length - 1 ? '10px' : 0,
            }}>
              {redigerar ? (
                <div>
                  {redigerarMoteFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 10px', fontFamily: "'Inter', sans-serif" }}>{redigerarMoteFel}</p>}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {valbaraTyper.map(t => (
                      <button
                        key={t}
                        onClick={() => setRedigerarMoteUtkast(u => ({ ...u, typ: t }))}
                        style={{
                          fontFamily: "'Inter', sans-serif", fontSize: '12px', fontWeight: 500,
                          padding: '6px 12px', borderRadius: '2px', cursor: 'pointer',
                          border: `1px solid ${redigerarMoteUtkast.typ === t ? TEMA.accent : TEMA.border}`,
                          background: redigerarMoteUtkast.typ === t ? TEMA.accent : 'transparent',
                          color: redigerarMoteUtkast.typ === t ? TEMA.onAccent : TEMA.inkSoft,
                        }}
                      >
                        {AKTIVITETS_TYP_INFO[t].etikett}
                      </button>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={labelStyle}>Datum</label>
                      <input
                        type="date"
                        value={redigerarMoteUtkast.datum}
                        onChange={e => setRedigerarMoteUtkast(u => ({ ...u, datum: e.target.value }))}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{redigerarMoteUtkast.typ === 'paminnelse' ? 'Påminn mig den' : 'Nästa kontakt'}</label>
                      <input
                        type="date"
                        value={redigerarMoteUtkast.nästa_kontakt}
                        onChange={e => setRedigerarMoteUtkast(u => ({ ...u, nästa_kontakt: e.target.value }))}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: visarVinfält ? '14px' : '10px' }}>
                    <label style={labelStyle}>{redigerarMoteUtkast.typ === 'paminnelse' ? 'Påminnelsetext' : 'Notat'}</label>
                    <textarea
                      value={redigerarMoteUtkast.notat}
                      onChange={e => setRedigerarMoteUtkast(u => ({ ...u, notat: e.target.value }))}
                      rows={2}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>

                  {visarVinfält && (
                    <div style={{ marginBottom: '14px' }}>
                      <p style={{ ...labelStyle, marginBottom: '8px' }}>Provade viner</p>
                      {laddarRedigerarMote && <p style={{ fontSize: '13px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>}
                      {!laddarRedigerarMote && redigerarMoteProvningar.map((p, i) => (
                        <div key={i} style={{ background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '6px', padding: '10px', marginBottom: '8px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 90px 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                            <input
                              placeholder="Vinets namn"
                              value={p.vin_namn}
                              onChange={e => uppdateraRedigeraProvning(i, 'vin_namn', e.target.value)}
                              style={inputStyle}
                            />
                            <input
                              placeholder="Pris"
                              inputMode="decimal"
                              value={p.pris}
                              onChange={e => uppdateraRedigeraProvning(i, 'pris', e.target.value)}
                              style={inputStyle}
                            />
                            <select value={p.reaktion} onChange={e => uppdateraRedigeraProvning(i, 'reaktion', e.target.value)} style={inputStyle}>
                              <option value="gillade">Gillade</option>
                              <option value="neutral">Neutral</option>
                              <option value="nej">Nej</option>
                            </select>
                            <select value={p.pris_accepterat} onChange={e => uppdateraRedigeraProvning(i, 'pris_accepterat', e.target.value)} style={inputStyle}>
                              <option value="ja">Pris: Ja</option>
                              <option value="nej">Pris: Nej</option>
                              <option value="vet ej">Pris: Vet ej</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              placeholder="Kommentar (valfritt)"
                              value={p.kommentar}
                              onChange={e => uppdateraRedigeraProvning(i, 'kommentar', e.target.value)}
                              style={{ ...inputStyle, flex: 1 }}
                            />
                            <button onClick={() => taBortRedigeraProvning(i)} style={{ ...btnGhostStyle, fontSize: '11px', padding: '4px 10px' }}>
                              Ta bort
                            </button>
                          </div>
                        </div>
                      ))}
                      {!laddarRedigerarMote && (
                        <button onClick={läggTillRedigeraProvning} style={btnGhostStyle}>
                          + Lägg till vin
                        </button>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={sparaRedigeraMote} disabled={spararMote} style={{ ...btnPrimärStyle, opacity: spararMote ? 0.6 : 1 }}>
                        {spararMote ? 'Sparar...' : 'Spara'}
                      </button>
                      <button onClick={avbrytRedigeraMote} disabled={spararMote} style={btnGhostStyle}>
                        Avbryt
                      </button>
                    </div>
                    <button
                      onClick={() => raderaMote(m)}
                      disabled={raderarMoteId === m.mote_id}
                      style={{ ...btnGhostStyle, color: '#D9695F', borderColor: '#D9695F' }}
                    >
                      {raderarMoteId === m.mote_id ? 'Tar bort...' : 'Ta bort aktivitet'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', letterSpacing: '0.04em',
                      padding: '3px 10px', borderRadius: '2px', border: `1px solid ${info.färg}`, color: info.färg,
                    }}>
                      {info.etikett}
                    </span>
                    {ogranskad && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', letterSpacing: '0.04em',
                        padding: '3px 10px', borderRadius: '2px', border: `1px solid ${TEMA.accent}`, color: TEMA.accent,
                      }}>
                        OGRANSKAD
                      </span>
                    )}
                    <span style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink }}>{m.datum}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                      {ogranskad && (
                        <button onClick={() => snabbGranska(m)} disabled={snabbgranskarId === m.mote_id} style={{ ...btnGhostStyle, fontSize: '11px', padding: '4px 10px' }}>
                          {snabbgranskarId === m.mote_id ? 'Sparar...' : 'Markera som granskad'}
                        </button>
                      )}
                      <button onClick={() => börjaRedigeraMote(m)} style={{ ...btnGhostStyle, fontSize: '11px', padding: '4px 10px' }}>
                        Redigera
                      </button>
                    </span>
                  </div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Säljare: {m.säljare}</p>
                  {m.notat && <p style={{ margin: '0 0 4px', fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{m.notat}</p>}
                                    {(m as any).antal_provningar > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        onClick={() => växlaProvningar(m)}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.accent,
                          display: 'flex', alignItems: 'center', gap: '6px',
                        }}
                      >
                        {(m as any).antal_provningar} viner provade
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{
                          transform: öppnaProvningar[m.mote_id] ? 'rotate(90deg)' : 'none',
                          transition: 'transform 0.15s ease',
                        }}>
                          <path d="M2 1L6 4L2 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>

                      {öppnaProvningar[m.mote_id] === 'laddar' && (
                        <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
                      )}

                      {Array.isArray(öppnaProvningar[m.mote_id]) && (
                        <div style={{ marginTop: '10px', borderTop: `1px solid ${TEMA.border}` }}>
                          {<>
                          {!ärMobil && <div style={{
                            display: 'grid', gridTemplateColumns: VINKOLUMNER, gap: '12px', padding: '8px 0 6px',
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', color: TEMA.inkFaint,
                            textTransform: 'uppercase', borderBottom: `1px solid ${TEMA.border}`,
                          }}>
                            <span /><span>Producent</span><span>Vin</span><span>Årgång</span>
                            <span style={{ textAlign: 'right' }}>Listpris</span>
                            <span style={{ textAlign: 'right' }}>Diskuterat pris</span>
                            <span>Utfall</span>
                          </div>}
                          {(öppnaProvningar[m.mote_id] as Provning[]).map((p, i) => {
                            const d = delaVin(p);
                            if (ärMobil) {
                              const pd = prisdelar(p);
                              return (
                                <div key={i} style={{
                                  padding: '12px 0', borderBottom: `1px solid ${TEMA.border}`,
                                  fontFamily: "'Inter', sans-serif", fontSize: '13.5px',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{
                                      fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px',
                                      padding: '1px 6px', borderRadius: '2px', flexShrink: 0,
                                      border: `1px solid ${p.reaktion === 'gillade' ? TEMA.positive : p.reaktion === 'nej' ? '#D9695F' : TEMA.border}`,
                                      color: p.reaktion === 'gillade' ? TEMA.positive : p.reaktion === 'nej' ? '#D9695F' : TEMA.inkFaint,
                                    }}>
                                      {p.reaktion === 'gillade' ? 'GILLADE' : p.reaktion === 'nej' ? 'NEJ' : 'NEUTRAL'}
                                    </span>
                                    <span style={{ fontSize: '12.5px', color: TEMA.inkSoft, minWidth: 0 }}>{d.producent}</span>
                                  </div>
                                  <div style={{ color: TEMA.ink }}>
                                    {d.vin}
                                    {d.årgång && <span style={{ color: TEMA.inkSoft }}> {d.årgång}</span>}
                                    {d.format && d.format !== '75 cl' && <span style={{ color: TEMA.inkFaint }}> · {d.format}</span>}
                                  </div>
                                  {p.kommentar && (
                                    <div style={{ fontSize: '12.5px', color: TEMA.inkFaint, marginTop: '2px' }}>{p.kommentar}</div>
                                  )}
                                  {(pd.lista || pd.pris) && (
                                    <div style={{
                                      marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '2px 14px',
                                      fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint,
                                    }}>
                                      {pd.lista ? <span style={{ whiteSpace: 'nowrap' }}>Listpris {pd.lista} kr</span> : null}
                                      {pd.pris && (
                                        <span style={{ color: TEMA.ink, whiteSpace: 'nowrap' }}>
                                          Diskuterat {pd.pris} kr
                                          {pd.skillnad !== null && (
                                            <span style={{ marginLeft: '6px', color: (pd.skillnad as number) < 0 ? TEMA.accent : TEMA.inkSoft }}>
                                              {(pd.skillnad as number) > 0 ? '+' : '−'}{Math.abs(pd.skillnad as number)} %
                                            </span>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {p.utfall && <div style={{ marginTop: '6px' }}><Utfall u={p.utfall} /></div>}
                                </div>
                              );
                            }
                            return (
                              <div key={i} style={{
                                display: 'grid', gridTemplateColumns: VINKOLUMNER, gap: '12px', alignItems: 'baseline',
                                padding: '8px 0', borderBottom: `1px solid ${TEMA.border}`,
                                fontFamily: "'Inter', sans-serif", fontSize: '13px',
                              }}>
                                <span style={{
                                  fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px',
                                  padding: '2px 7px', borderRadius: '2px', textAlign: 'center',
                                  border: `1px solid ${p.reaktion === 'gillade' ? TEMA.positive : p.reaktion === 'nej' ? '#D9695F' : TEMA.border}`,
                                  color: p.reaktion === 'gillade' ? TEMA.positive : p.reaktion === 'nej' ? '#D9695F' : TEMA.inkFaint,
                                }}>
                                  {p.reaktion === 'gillade' ? 'GILLADE' : p.reaktion === 'nej' ? 'NEJ' : 'NEUTRAL'}
                                </span>
                                <span style={{ color: TEMA.inkSoft, minWidth: 0 }}>{d.producent}</span>
                                <span style={{ color: TEMA.ink, minWidth: 0 }}>
                                  {d.vin}
                                  {d.format && d.format !== '75 cl' && <span style={{ color: TEMA.inkFaint }}> · {d.format}</span>}
                                  {p.kommentar && (
                                    <span style={{ display: 'block', fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>{p.kommentar}</span>
                                  )}
                                </span>
                                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkSoft }}>{d.årgång}</span>
                                <span style={{ textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint }}>
                                  {prisdelar(p).lista ? `${prisdelar(p).lista} kr` : ''}
                                </span>
                                <span style={{ textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.ink, whiteSpace: 'nowrap' }}>
                                  {prisdelar(p).pris ? `${prisdelar(p).pris} kr` : ''}
                                  {prisdelar(p).skillnad !== null && (
                                    <span style={{ marginLeft: '6px', color: (prisdelar(p).skillnad as number) < 0 ? TEMA.accent : TEMA.inkSoft }}>
                                      {(prisdelar(p).skillnad as number) > 0 ? '+' : '−'}{Math.abs(prisdelar(p).skillnad as number)} %
                                    </span>
                                  )}
                                </span>
                                <Utfall u={p.utfall} />
                              </div>
                            );
                          })}
                          </>}
                        </div>
                      )}
                    </div>
                  )}
                  {m.nästa_kontakt && (
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                      {m.typ === 'paminnelse' ? 'Påminn den' : 'Nästa kontakt'}: {m.nästa_kontakt}
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {öppnaOrdrar.length > 0 && (
        <>
          <p style={{ ...sektionsLabel, marginTop: '28px' }}>
            {öppnaOrdrar.length === 1 ? 'Öppen order' : 'Öppna ordrar'} (Fortnox, exkl. moms)
          </p>
          <div style={{ ...kortStyle, borderColor: TEMA.accent }}>
            {öppnaOrdrar.map((o, i) => (
              <div key={o.order} style={{
                padding: i === 0 ? '0 0 4px' : '16px 0 4px',
                borderTop: i === 0 ? 'none' : `1px solid ${TEMA.border}`,
                marginTop: i === 0 ? 0 : '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
                      Order #{o.order}
                    </p>
                    <span style={{ fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                      Lagd {o.datum || 'okänt datum'}
                    </span>
                    <span style={chipStyle(TEMA.accent)}>Väntar på fakturering</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                    {o.belopp.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr
                  </p>
                </div>
                {o.rader.length === 0 && (
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Inga artikelrader på ordern.</p>
                )}
                {o.rader.map((r, idx) => (
                  <div key={`${r.artikel_nr}-${idx}`} style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '8px 0',
                  }}>
                    <p style={{ margin: 0, minWidth: 0, fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                      {r.namn} <span style={{ color: TEMA.inkFaint }}>· {r.antal} st</span>
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {r.belopp.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      <p style={{ ...sektionsLabel, marginTop: '28px' }}>Köphistorik</p>
      <div style={kortStyle}>
        {laddarArtiklar && <p style={{ color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Laddar köphistorik...</p>}
        {artiklarFel && <p style={{ color: '#D9695F', fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>{artiklarFel}</p>}
        {!laddarArtiklar && !artiklarFel && artiklar.length === 0 && (
          <p style={{ color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>{öppnaOrdrar.length > 0 ? 'Inga fakturerade köp ännu.' : 'Inga köp ännu.'}</p>
        )}
        {!laddarArtiklar && artiklar.slice(0, visaAntal).map((a, idx, visade) => (
          <div key={a.namn} style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
            padding: '12px 0', borderBottom: idx < visade.length - 1 ? `1px solid ${TEMA.border}` : 'none',
          }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{a.namn}</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                {a.antal} st · Senast köpt {a.senast_köpt}
              </p>
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap', flexShrink: 0 }}>
              {a.belopp.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr
            </p>
          </div>
        ))}
        {!laddarArtiklar && artiklar.length > 10 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            {artiklar.length > visaAntal && (
              <button onClick={() => setVisaAntal(n => n + 10)} style={btnGhostStyle}>
                Visa 10 till ({artiklar.length - visaAntal} kvar)
              </button>
            )}
            {visaAntal > 10 && (
              <button onClick={() => setVisaAntal(n => Math.max(10, n - 10))} style={btnGhostStyle}>
                Visa färre
              </button>
            )}
          </div>
        )}
      </div>

      {fakturor.length > 0 && (
        <>
          <p style={{ ...sektionsLabel, marginTop: '28px' }}>Fakturahistorik (Fortnox, exkl. moms)</p>
          <div style={kortStyle}>
            {[...fakturor].sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime()).map(f => (
              <div key={f.faktura_nr} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 0', borderBottom: `1px solid ${TEMA.border}`,
              }}>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
                    Faktura #{f.faktura_nr}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                    {f.datum} · Förfaller {f.förfallodatum}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {f.total.toLocaleString('sv-SE')} kr
                  </p>
                  <span style={chipStyle(f.betald ? TEMA.positive : TEMA.inkFaint)}>
                    {f.betald ? 'Betald' : 'Obetald'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {kund && <KundHistorik kundId={kund.kund_id} />}
    </div>
  );
}
